import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Building2, UserPlus, Users, Package, Plus, Loader2, Copy, Check, Truck, MapPin, Hash } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Receivers = () => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [orgs, setOrgs] = useState([]);
  const [users, setUsers] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrg, setSelectedOrg] = useState(null);

  // Dialog states
  const [showAddOrg, setShowAddOrg] = useState(false);
  const [showAddUser, setShowAddUser] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copiedPwd, setCopiedPwd] = useState(false);

  // Tab: receivers | shippers
  const [activeTab, setActiveTab] = useState('receivers');

  // Shipper states
  const [shipperOrgs, setShipperOrgs] = useState([]);
  const [shipperUsers, setShipperUsers] = useState([]);
  const [selectedShipperOrg, setSelectedShipperOrg] = useState(null);
  const [showAddShipperOrg, setShowAddShipperOrg] = useState(false);
  const [showAddShipperUser, setShowAddShipperUser] = useState(false);
  const [shipperOrgForm, setShipperOrgForm] = useState({ name: '', gln: '' });
  const [shipperUserForm, setShipperUserForm] = useState({ name: '', email: '', password: '', pin_code: '' });

  // Form states
  const [orgForm, setOrgForm] = useState({ name: '', gs1_company_prefix: '' });
  const [userForm, setUserForm] = useState({ name: '', email: '', password: '' });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [orgsRes, usersRes, shipmentsRes] = await Promise.all([
        axios.get(`${API}/organizations`, { headers }),
        axios.get(`${API}/users`, { headers }),
        axios.get(`${API}/shipments`, { headers }),
      ]);
      const receiverOrgs = orgsRes.data.filter(o => o.type === 'receiver');
      const shipperOrgsList = orgsRes.data.filter(o => o.type === 'shipper');
      setOrgs(receiverOrgs);
      setShipperOrgs(shipperOrgsList);
      setUsers(usersRes.data.filter(u => u.role === 'receiver'));
      setShipperUsers(usersRes.data.filter(u => u.role === 'shipper'));
      setShipments(shipmentsRes.data);
      if (receiverOrgs.length > 0 && !selectedOrg) {
        setSelectedOrg(receiverOrgs[0]);
      }
      if (shipperOrgsList.length > 0 && !selectedShipperOrg) {
        setSelectedShipperOrg(shipperOrgsList[0]);
      }
    } catch {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreateOrg = async () => {
    if (!orgForm.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/organizations`, {
        name: orgForm.name,
        type: 'receiver',
        gs1_company_prefix: orgForm.gs1_company_prefix || null,
      }, { headers });
      toast.success(`Receiver "${orgForm.name}" created`);
      setShowAddOrg(false);
      setOrgForm({ name: '', gs1_company_prefix: '' });
      setSelectedOrg(res.data);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create receiver');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateUser = async () => {
    if (!userForm.name.trim() || !userForm.email.trim() || !userForm.password.trim()) {
      toast.error('All fields are required'); return;
    }
    if (userForm.password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/users`, {
        name: userForm.name,
        email: userForm.email,
        password: userForm.password,
        role: 'receiver',
        organization_id: selectedOrg.id,
        organization_type: 'receiver',
      }, { headers });
      toast.success(`User "${userForm.name}" created`);
      setShowAddUser(false);
      setUserForm({ name: '', email: '', password: '' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create user');
    } finally {
      setSaving(false);
    }
  };

  const orgUsers = users.filter(u => u.organization_id === selectedOrg?.id);
  const orgShipments = shipments.filter(s => s.receiver_id === selectedOrg?.id);

  const copyCredentials = () => {
    const text = `Login: ${userForm.email}\nPassword: ${userForm.password}\nURL: ${window.location.origin}/login`;
    navigator.clipboard.writeText(text);
    setCopiedPwd(true);
    setTimeout(() => setCopiedPwd(false), 2000);
  };

  const handleCreateShipperOrg = async () => {
    if (!shipperOrgForm.name.trim()) { toast.error('Name is required'); return; }
    if (!shipperOrgForm.gln.trim()) { toast.error('GLN is required'); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/organizations`, {
        name: shipperOrgForm.name,
        type: 'shipper',
        gln: shipperOrgForm.gln,
      }, { headers });
      toast.success(`Shipper "${shipperOrgForm.name}" created`);
      setShowAddShipperOrg(false);
      setShipperOrgForm({ name: '', gln: '' });
      setSelectedShipperOrg(res.data);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create shipper');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateShipperUser = async () => {
    if (!shipperUserForm.name.trim() || !shipperUserForm.email.trim() || !shipperUserForm.password.trim()) {
      toast.error('Name, email, and password are required'); return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/users`, {
        name: shipperUserForm.name,
        email: shipperUserForm.email,
        password: shipperUserForm.password,
        role: 'shipper',
        organization_id: selectedShipperOrg.id,
        organization_type: 'shipper',
        pin_code: shipperUserForm.pin_code || null,
      }, { headers });
      toast.success(`Shipper user "${shipperUserForm.name}" created`);
      setShowAddShipperUser(false);
      setShipperUserForm({ name: '', email: '', password: '', pin_code: '' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create user');
    } finally {
      setSaving(false);
    }
  };

  const copyShipperCredentials = () => {
    const text = `Login: ${shipperUserForm.email}\nPassword: ${shipperUserForm.password}${shipperUserForm.pin_code ? `\nPIN: ${shipperUserForm.pin_code}` : ''}\nShipper App: ${window.location.origin}/shipper`;
    navigator.clipboard.writeText(text);
    setCopiedPwd(true);
    setTimeout(() => setCopiedPwd(false), 2000);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="receivers-page">
      {/* Header with Tabs */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Partners</h1>
          <p className="text-slate-500 text-sm">Manage receivers and shippers</p>
        </div>
        {activeTab === 'receivers' && (
          <Button onClick={() => setShowAddOrg(true)} data-testid="add-receiver-org-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Receiver
          </Button>
        )}
        {activeTab === 'shippers' && (
          <Button onClick={() => setShowAddShipperOrg(true)} data-testid="add-shipper-org-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Shipper
          </Button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 rounded-lg p-1 w-fit">
        <button
          className={`px-4 py-2 text-sm font-medium rounded-md transition ${activeTab === 'receivers' ? 'bg-white shadow text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}
          onClick={() => setActiveTab('receivers')}
          data-testid="receivers-tab"
        >
          <Building2 className="w-4 h-4 inline mr-1.5" />Receivers ({orgs.length})
        </button>
        <button
          className={`px-4 py-2 text-sm font-medium rounded-md transition ${activeTab === 'shippers' ? 'bg-white shadow text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}
          onClick={() => setActiveTab('shippers')}
          data-testid="shippers-tab"
        >
          <Truck className="w-4 h-4 inline mr-1.5" />Shippers ({shipperOrgs.length})
        </button>
      </div>

      {activeTab === 'receivers' && (
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Org List */}
        <div className="space-y-3">
          <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Receiver Companies</h2>
          {orgs.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="py-8 text-center">
                <Building2 className="w-10 h-10 mx-auto text-slate-300 mb-3" />
                <p className="text-sm text-slate-500">No receivers yet</p>
                <Button variant="outline" size="sm" className="mt-3" onClick={() => setShowAddOrg(true)}>
                  <Plus className="w-3 h-3 mr-1" /> Add first receiver
                </Button>
              </CardContent>
            </Card>
          ) : (
            orgs.map(org => {
              const userCount = users.filter(u => u.organization_id === org.id).length;
              const shipCount = shipments.filter(s => s.receiver_id === org.id).length;
              const isSelected = selectedOrg?.id === org.id;
              return (
                <Card
                  key={org.id}
                  className={`cursor-pointer transition-all ${isSelected ? 'ring-2 ring-blue-500 bg-blue-50/50' : 'hover:border-blue-200'}`}
                  onClick={() => setSelectedOrg(org)}
                  data-testid={`receiver-org-${org.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-purple-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900 truncate">{org.name}</p>
                        <div className="flex items-center gap-3 text-xs text-slate-500">
                          <span className="flex items-center gap-1"><Users className="w-3 h-3" />{userCount}</span>
                          <span className="flex items-center gap-1"><Package className="w-3 h-3" />{shipCount}</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>

        {/* Detail Panel */}
        <div className="lg:col-span-2 space-y-6">
          {selectedOrg ? (
            <>
              {/* Users Section */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-3">
                  <div>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Users className="w-5 h-5 text-blue-500" />
                      {selectedOrg.name} — Users
                    </CardTitle>
                    <CardDescription>People who can log in and view shipments for this receiver</CardDescription>
                  </div>
                  <Button size="sm" onClick={() => { setUserForm({ name: '', email: '', password: '' }); setShowAddUser(true); }} data-testid="add-receiver-user-btn">
                    <UserPlus className="w-4 h-4 mr-1" /> Add User
                  </Button>
                </CardHeader>
                <CardContent>
                  {orgUsers.length === 0 ? (
                    <div className="text-center py-6 text-slate-400">
                      <UserPlus className="w-8 h-8 mx-auto mb-2" />
                      <p className="text-sm">No users yet. Add someone to give them access.</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {orgUsers.map(u => (
                        <div key={u.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg" data-testid={`receiver-user-${u.id}`}>
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-purple-500 flex items-center justify-center text-white text-xs font-bold">
                              {u.name?.charAt(0)?.toUpperCase()}
                            </div>
                            <div>
                              <p className="text-sm font-medium text-slate-900">{u.name}</p>
                              <p className="text-xs text-slate-500">{u.email}</p>
                            </div>
                          </div>
                          <Badge className="bg-purple-100 text-purple-700">Receiver</Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Shipments Section */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Package className="w-5 h-5 text-green-500" />
                    Shipments to {selectedOrg.name}
                  </CardTitle>
                  <CardDescription>All shipments linked to this receiver</CardDescription>
                </CardHeader>
                <CardContent>
                  {orgShipments.length === 0 ? (
                    <div className="text-center py-6 text-slate-400">
                      <Truck className="w-8 h-8 mx-auto mb-2" />
                      <p className="text-sm">No shipments linked to this receiver yet.</p>
                      <p className="text-xs mt-1">Assign this receiver when creating a shipment.</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {orgShipments.map(s => (
                        <div key={s.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className={`w-2 h-2 rounded-full ${s.status === 'completed' ? 'bg-green-500' : s.status === 'in_transit' ? 'bg-blue-500' : 'bg-yellow-500'}`} />
                            <div>
                              <p className="text-sm font-mono font-medium">{s.sscc?.slice(-12)}</p>
                              <p className="text-xs text-slate-500 flex items-center gap-1">
                                <MapPin className="w-3 h-3" />{s.origin} → {s.destination}
                              </p>
                            </div>
                          </div>
                          <Badge className={s.status === 'completed' ? 'bg-green-100 text-green-700' : s.status === 'in_transit' ? 'bg-blue-100 text-blue-700' : 'bg-yellow-100 text-yellow-700'}>
                            {s.status?.replace('_', ' ')}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          ) : (
            <Card className="border-dashed">
              <CardContent className="py-16 text-center">
                <Building2 className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">Select a receiver company to manage users and view shipments</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
      )}

      {/* Shippers Tab */}
      {activeTab === 'shippers' && (
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Shipper Org List */}
        <div className="space-y-3">
          <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Shipper Companies</h2>
          {shipperOrgs.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="py-8 text-center">
                <Truck className="w-10 h-10 mx-auto text-slate-300 mb-3" />
                <p className="text-sm text-slate-500">No shippers yet</p>
                <Button variant="outline" size="sm" className="mt-3" onClick={() => setShowAddShipperOrg(true)}>
                  <Plus className="w-3 h-3 mr-1" /> Add first shipper
                </Button>
              </CardContent>
            </Card>
          ) : (
            shipperOrgs.map(org => {
              const userCount = shipperUsers.filter(u => u.organization_id === org.id).length;
              const isSelected = selectedShipperOrg?.id === org.id;
              return (
                <Card
                  key={org.id}
                  className={`cursor-pointer transition-all ${isSelected ? 'ring-2 ring-blue-500 bg-blue-50/50' : 'hover:border-blue-200'}`}
                  onClick={() => setSelectedShipperOrg(org)}
                  data-testid={`shipper-org-${org.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                        <Truck className="w-5 h-5 text-orange-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900 truncate">{org.name}</p>
                        <div className="flex items-center gap-3 text-xs text-slate-500">
                          <span className="flex items-center gap-1"><Users className="w-3 h-3" />{userCount}</span>
                          {org.gln && <span className="font-mono">GLN: {org.gln}</span>}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>

        {/* Shipper Detail */}
        {selectedShipperOrg ? (
          <div className="lg:col-span-2 space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>{selectedShipperOrg.name}</CardTitle>
                    <CardDescription>
                      {selectedShipperOrg.gln && <span className="font-mono">GLN: {selectedShipperOrg.gln}</span>}
                    </CardDescription>
                  </div>
                  <Button size="sm" onClick={() => setShowAddShipperUser(true)} data-testid="add-shipper-user-btn">
                    <UserPlus className="w-4 h-4 mr-2" /> Add User
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <h3 className="text-sm font-semibold text-slate-700">Users</h3>
                  {shipperUsers.filter(u => u.organization_id === selectedShipperOrg.id).length === 0 ? (
                    <p className="text-sm text-slate-400">No users yet</p>
                  ) : (
                    shipperUsers.filter(u => u.organization_id === selectedShipperOrg.id).map(u => (
                      <div key={u.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                        <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center text-sm font-bold text-orange-600">
                          {u.name?.charAt(0)?.toUpperCase()}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-900">{u.name}</p>
                          <p className="text-xs text-slate-500">{u.email}</p>
                        </div>
                        {u.pin_code && <Badge variant="outline" className="ml-auto text-xs">PIN set</Badge>}
                      </div>
                    ))
                  )}
                </div>
                <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                  <p className="text-xs text-blue-700">
                    <Truck className="w-3 h-3 inline mr-1" />
                    Shipper app URL: <span className="font-mono">{window.location.origin}/shipper</span>
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        ) : (
          <div className="lg:col-span-2">
            <Card>
              <CardContent className="py-12 text-center">
                <Truck className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                <p className="text-slate-500">Select a shipper company to manage users</p>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
      )}

      {/* Add Receiver Organization Dialog */}
      <Dialog open={showAddOrg} onOpenChange={setShowAddOrg}>
        <DialogContent className="sm:max-w-md" data-testid="add-receiver-org-dialog">
          <DialogHeader>
            <DialogTitle>Add Receiver Company</DialogTitle>
            <DialogDescription>Create a new receiver organization that can receive shipments</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <Label>Company Name</Label>
              <Input
                placeholder="e.g. Rema 1000"
                value={orgForm.name}
                onChange={e => setOrgForm({ ...orgForm, name: e.target.value })}
                data-testid="org-name-input"
              />
            </div>
            <div className="space-y-1">
              <Label>GS1 Company Prefix (optional)</Label>
              <Input
                placeholder="e.g. 7070456"
                value={orgForm.gs1_company_prefix}
                onChange={e => setOrgForm({ ...orgForm, gs1_company_prefix: e.target.value })}
                data-testid="org-gs1-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddOrg(false)}>Cancel</Button>
            <Button onClick={handleCreateOrg} disabled={saving} data-testid="org-submit-btn">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Create Receiver
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add User Dialog */}
      <Dialog open={showAddUser} onOpenChange={setShowAddUser}>
        <DialogContent className="sm:max-w-md" data-testid="add-receiver-user-dialog">
          <DialogHeader>
            <DialogTitle>Add User to {selectedOrg?.name}</DialogTitle>
            <DialogDescription>Create login credentials for this receiver</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <Label>Full Name</Label>
              <Input
                placeholder="e.g. Ola Nordmann"
                value={userForm.name}
                onChange={e => setUserForm({ ...userForm, name: e.target.value })}
                data-testid="user-name-input"
              />
            </div>
            <div className="space-y-1">
              <Label>Email</Label>
              <Input
                type="email"
                placeholder="e.g. ola@rema1000.no"
                value={userForm.email}
                onChange={e => setUserForm({ ...userForm, email: e.target.value })}
                data-testid="user-email-input"
              />
            </div>
            <div className="space-y-1">
              <Label>Temporary Password</Label>
              <Input
                type="text"
                placeholder="Min 6 characters"
                value={userForm.password}
                onChange={e => setUserForm({ ...userForm, password: e.target.value })}
                data-testid="user-password-input"
              />
              <p className="text-xs text-slate-500">Share this with the user. They can change it after login.</p>
            </div>
            {userForm.email && userForm.password && (
              <Button variant="outline" size="sm" className="w-full" onClick={copyCredentials} data-testid="copy-credentials-btn">
                {copiedPwd ? <Check className="w-4 h-4 mr-2 text-green-500" /> : <Copy className="w-4 h-4 mr-2" />}
                {copiedPwd ? 'Copied!' : 'Copy Credentials'}
              </Button>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddUser(false)}>Cancel</Button>
            <Button onClick={handleCreateUser} disabled={saving} data-testid="user-submit-btn">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Create User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Shipper Organization Dialog */}
      <Dialog open={showAddShipperOrg} onOpenChange={setShowAddShipperOrg}>
        <DialogContent className="sm:max-w-md" data-testid="add-shipper-org-dialog">
          <DialogHeader>
            <DialogTitle>Add Shipper Company</DialogTitle>
            <DialogDescription>Create a shipper organization with a GLN</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <Label>Company Name</Label>
              <Input placeholder="e.g. FishCo AS" value={shipperOrgForm.name} onChange={e => setShipperOrgForm({ ...shipperOrgForm, name: e.target.value })} data-testid="shipper-org-name-input" />
            </div>
            <div className="space-y-1">
              <Label>GLN (Global Location Number)</Label>
              <Input placeholder="13-digit GLN" value={shipperOrgForm.gln} onChange={e => setShipperOrgForm({ ...shipperOrgForm, gln: e.target.value })} data-testid="shipper-org-gln-input" maxLength={13} />
              <p className="text-xs text-slate-500">Required — identifies the shipper location</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddShipperOrg(false)}>Cancel</Button>
            <Button onClick={handleCreateShipperOrg} disabled={saving} data-testid="shipper-org-submit-btn">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Create Shipper
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Shipper User Dialog */}
      <Dialog open={showAddShipperUser} onOpenChange={setShowAddShipperUser}>
        <DialogContent className="sm:max-w-md" data-testid="add-shipper-user-dialog">
          <DialogHeader>
            <DialogTitle>Add Shipper User to {selectedShipperOrg?.name}</DialogTitle>
            <DialogDescription>Create login credentials for this shipper</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <Label>Full Name</Label>
              <Input placeholder="e.g. Per Hansen" value={shipperUserForm.name} onChange={e => setShipperUserForm({ ...shipperUserForm, name: e.target.value })} data-testid="shipper-user-name-input" />
            </div>
            <div className="space-y-1">
              <Label>Email</Label>
              <Input type="email" placeholder="e.g. per@fishco.no" value={shipperUserForm.email} onChange={e => setShipperUserForm({ ...shipperUserForm, email: e.target.value })} data-testid="shipper-user-email-input" />
            </div>
            <div className="space-y-1">
              <Label>Password</Label>
              <Input type="text" placeholder="Min 6 characters" value={shipperUserForm.password} onChange={e => setShipperUserForm({ ...shipperUserForm, password: e.target.value })} data-testid="shipper-user-password-input" />
            </div>
            <div className="space-y-1">
              <Label>PIN Code (optional — for mobile quick login)</Label>
              <Input type="tel" inputMode="numeric" placeholder="4-6 digits" value={shipperUserForm.pin_code} onChange={e => setShipperUserForm({ ...shipperUserForm, pin_code: e.target.value.replace(/\D/g, '').slice(0, 6) })} data-testid="shipper-user-pin-input" maxLength={6} />
            </div>
            {shipperUserForm.email && shipperUserForm.password && (
              <Button variant="outline" size="sm" className="w-full" onClick={copyShipperCredentials} data-testid="copy-shipper-credentials-btn">
                {copiedPwd ? <Check className="w-4 h-4 mr-2 text-green-500" /> : <Copy className="w-4 h-4 mr-2" />}
                {copiedPwd ? 'Copied!' : 'Copy Credentials'}
              </Button>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddShipperUser(false)}>Cancel</Button>
            <Button onClick={handleCreateShipperUser} disabled={saving} data-testid="shipper-user-submit-btn">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Create Shipper User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Receivers;
